import { ReactNode, useMemo, useState } from 'react'
import { TProgrammedMessage } from './programmed-message.type'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'src/providers/notification.provider'
import {
  CurecallConfirmationModal,
  CurecallGroupedMultiSelect,
  CurecallGroupedMultiSelectGroup,
  CurecallMultiSelectItem,
  CurecallList,
  CurecallListHeaders,
  CurecallVariableModal,
  TTextareaVariable,
  CurecallModalContainer,
} from '@honestica/curecall-ui'
import { ProjectService } from 'src/services/project.service'
import { isConflictError } from 'src/utils/http.util'
import { isContentPartial } from './not-editable-message.helper'
import {
  ActionsCell,
  MedEventsCell,
  MessageContentCell,
  MessageContentWithCopyButton,
  ProgrammedDateCell,
  TextCell,
} from './programmed-message-cells.component'
import {
  TOrderBy,
  EList,
  getListPreferedOrder,
  setListPreferedOrder,
} from 'src/utils/list-order-preference.util'
import { useVariables } from '../../../../_components/variables-context'
import { compareIsoDurations } from './programmed-message.helper'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { DuplicateProgrammedMessageModal } from './duplicate-programmed-message-modal.component'

type TProgrammedMessageColumn =
  | 'Id'
  | 'MessageName'
  | 'MedEventType'
  | 'ProgrammingDate'
  | 'MessageContents'
  | 'Actions'

type TTableRow = { [key in TProgrammedMessageColumn]: ReactNode }

type TSortableColumn = Extract<TProgrammedMessageColumn, 'Id' | 'ProgrammingDate'>
const COLUMN_SORTS: { [key in TSortableColumn]: any } = {
  Id: (messages: TProgrammedMessage[]) => [...messages].sort((msg1, msg2) => msg1.id - msg2.id),
  ProgrammingDate: (messages: TProgrammedMessage[]) =>
    [...messages].sort((msg1, msg2) =>
      compareIsoDurations(msg1.schedule.durationDiff, msg2.schedule.durationDiff),
    ),
}

export default function ProgrammedMessageList({
  programmedMessages,
}: Readonly<{
  programmedMessages: TProgrammedMessage[]
}>) {
  const defaultPreferedOrder: TOrderBy<TSortableColumn> = getListPreferedOrder(
    EList.MESSAGE_PROGRAMMED_LIST,
  ) || { column: 'ProgrammingDate', descOrder: false }

  const defaultSortedProgrammedMessages = defaultPreferedOrder.descOrder
    ? COLUMN_SORTS[defaultPreferedOrder.column as TSortableColumn](programmedMessages).reverse()
    : COLUMN_SORTS[defaultPreferedOrder.column as TSortableColumn](programmedMessages)

  return (
    <ProgrammedMessageListComponent
      defaultSortedProgrammedMessages={defaultSortedProgrammedMessages}
      defaultPreferedOrder={defaultPreferedOrder}
    />
  )
}

function ProgrammedMessageListComponent({
  defaultSortedProgrammedMessages,
  defaultPreferedOrder,
}: Readonly<{
  defaultSortedProgrammedMessages: TProgrammedMessage[]
  defaultPreferedOrder: TOrderBy<TSortableColumn>
}>) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { variableStore, contentVariables, defaultContentVariable } = useVariables()

  const { project, followups, getMedEventLabelById } = useCurrentProject()
  const queryClient = useQueryClient()
  const { notify } = useNotification()

  const [currentOrder, setCurrentOrder] = useState<TOrderBy<TSortableColumn>>(defaultPreferedOrder)
  const [programmedMessagesSorted, setProgrammedMessagesSorted] = useState<TProgrammedMessage[]>(
    defaultSortedProgrammedMessages,
  )
  const [selectedMedEventFilters, setSelectedMedEventFilters] = useState<CurecallMultiSelectItem[]>([])
  const [programmedMesssageToDelete, setProgrammedMesssageToDelete] = useState<TProgrammedMessage | null>(
    null,
  )
  const [variableToShow, setVariableToShow] = useState<TTextareaVariable | null>(null)
  const [programmedMessageToShow, setProgrammedMessageToShow] = useState<TProgrammedMessage | null>(null)
  const [programmedMessageToDuplicate, setProgrammedMessageToDuplicate] = useState<TProgrammedMessage | null>(
    null,
  )

  const deleteMessage = useMutation({
    mutationFn: (messageId: number) => {
      return ProjectService.deleteSelfConfigurationMessage(project.id, messageId)
    },
    onError: (error) => {
      notify(
        {
          title: t('pages.configuration.messages.error-title'),
          message: isConflictError(error)
            ? t('pages.configuration.messages.not-deletable')
            : t('pages.configuration.messages.error-body'),
          appearance: 'ERROR',
        },
        15000,
      )
      if (isConflictError(error)) {
        queryClient.invalidateQueries({ queryKey: ['programmed-messages', project.id] })
      }
    },
    onSuccess: (_data, messageIdDeleted) => {
      queryClient.setQueryData(['programmed-messages', project.id], (oldData: any) => {
        if (!oldData) return
        return oldData.filter(
          (programmedMessage: TProgrammedMessage) => programmedMessage.id !== messageIdDeleted,
        )
      })
      setProgrammedMessagesSorted((prev) => prev.filter((message) => message.id !== messageIdDeleted))
      notify(
        {
          title: t('pages.configuration.messages.success-title'),
          message: t('pages.configuration.messages.success-body'),
          appearance: 'SUCCESS',
        },
        15000,
      )
    },
  })

  const confirmDeleteMessage = async (messageId: number) => {
    setProgrammedMesssageToDelete(null)
    deleteMessage.mutate(messageId)
  }

  const medEventGroups = useMemo<CurecallGroupedMultiSelectGroup[]>(
    () =>
      followups
        .filter((followup) => (followup.configUI?.medEventsUI?.length ?? 0) > 0)
        .map((followup) => ({
          label: followup.name,
          value: followup.id,
          children: followup.configUI.medEventsUI.map((medEventUI) => ({
            value: medEventUI.MedEvent.id,
            label: getMedEventLabelById(medEventUI.MedEvent.id),
          })),
        })),
    [followups, getMedEventLabelById],
  )

  const filteredMessages = useMemo(() => {
    if (selectedMedEventFilters.length === 0) return programmedMessagesSorted
    const selectedIds = new Set(selectedMedEventFilters.map((item) => item.value))
    return programmedMessagesSorted.filter((message) =>
      message.medEventIds.some((medEventId) => selectedIds.has(medEventId)),
    )
  }, [programmedMessagesSorted, selectedMedEventFilters])

  const sortTableByColumn = (columnToSort: TSortableColumn) => {
    const shouldReverseOrder = currentOrder.column === columnToSort
    const newOrder = {
      column: columnToSort,
      descOrder: shouldReverseOrder ? !currentOrder.descOrder : false,
    }

    const colonneSort = COLUMN_SORTS[columnToSort]
    setProgrammedMessagesSorted((prev) =>
      newOrder.descOrder ? colonneSort(prev).reverse() : colonneSort(prev),
    )
    setCurrentOrder(newOrder)
    setListPreferedOrder(EList.MESSAGE_PROGRAMMED_LIST, newOrder)
  }

  const tableRows: Array<TTableRow> = filteredMessages.map(
    (programmedMessage: TProgrammedMessage, index: number) => ({
      Id: <TextCell>{programmedMessage.id}</TextCell>,
      MessageName: <TextCell>{programmedMessage.messageName}</TextCell>,
      MedEventType: <MedEventsCell medEventIds={programmedMessage.medEventIds} isFirstLine={index === 0} />,
      ProgrammingDate: <ProgrammedDateCell schedule={programmedMessage.schedule} />,
      MessageContents: (
        <MessageContentCell
          programmedMessage={programmedMessage}
          variableStore={variableStore}
          contentVariables={contentVariables}
          defaultContentVariable={defaultContentVariable}
          setVariableToShow={setVariableToShow}
          setProgrammedMessageToShow={setProgrammedMessageToShow}
        />
      ),
      Actions: (
        <ActionsCell
          programmedMessage={programmedMessage}
          onDeleteMessage={() => setProgrammedMesssageToDelete(programmedMessage)}
          onUpdateMessage={(id: number, followupId: number | null) =>
            navigate(`/${project.id}/configuration/followups/${followupId}/messages/${id}`)
          }
          onDuplicateMessage={() => setProgrammedMessageToDuplicate(programmedMessage)}
        />
      ),
    }),
  )

  const columnsData: CurecallListHeaders<TTableRow> = [
    {
      label: t('pages.configuration.messages.id'),
      key: 'Id',
      isSorted: true,
      onHeaderClick: () => sortTableByColumn('Id'),
    },
    { label: t('pages.configuration.messages.message-name'), key: 'MessageName' },
    { label: t('pages.configuration.messages.med-events'), key: 'MedEventType' },
    {
      label: t('pages.configuration.messages.programming-date'),
      key: 'ProgrammingDate',
      isSorted: true,
      onHeaderClick: () => sortTableByColumn('ProgrammingDate'),
    },
    { label: t('pages.configuration.messages.message-content'), key: 'MessageContents' },
    { label: t('pages.configuration.messages.actions'), key: 'Actions' },
  ]

  return (
    <div className="c-flex c-flex-col c-max-h-full c-pb-10">
      <header className="c-flex c-mb-6 c-items-end">
        <CurecallGroupedMultiSelect
          label={t('pages.configuration.filter-by-medevent')}
          placeholder={t('pages.configuration.choose-a-medevent')}
          groups={medEventGroups}
          selectedItems={selectedMedEventFilters}
          selectedElementsLabel={t('pages.configuration.filter-selected-medevents-label', {
            count: selectedMedEventFilters.length,
          })}
          onSelect={setSelectedMedEventFilters}
          searchPlaceholder={t('pages.configuration.filter-search-medevent')}
          noResultText={t('pages.configuration.filter-no-medevent-result')}
          clearSearchAriaLabel={t('pages.configuration.filter-clear-search')}
          openOptionsAriaLabel={t('pages.configuration.filter-open-options')}
          closeOptionsAriaLabel={t('pages.configuration.filter-close-options')}
          summaryTooltipPosition="right-start"
          inputClassName="tw-w-96"
          optionsClassName="tw-z-20 !c-max-h-80"
        />
      </header>

      <CurecallList
        rowsData={tableRows}
        headersData={columnsData}
        currentOrder={{ key: currentOrder.column, descOrder: currentOrder.descOrder }}
      />

      {programmedMessageToDuplicate && (
        <DuplicateProgrammedMessageModal
          isOpen={!!programmedMessageToDuplicate}
          onClose={() => setProgrammedMessageToDuplicate(null)}
          message={{
            id: programmedMessageToDuplicate.id,
            name: programmedMessageToDuplicate.messageName,
            followupId: programmedMessageToDuplicate.followupId,
          }}
        />
      )}

      {programmedMesssageToDelete && (
        <CurecallConfirmationModal
          color="ALERT"
          title={t('pages.configuration.messages.confirm-delete')}
          isOpen={!!programmedMesssageToDelete}
          cancelButtonLabel={t('common.actions.cancel')}
          confirmButtonLabel={t('common.actions.confirm')}
          onCancel={() => setProgrammedMesssageToDelete(null)}
          onConfirm={() => confirmDeleteMessage(programmedMesssageToDelete.id)}
        >
          {programmedMesssageToDelete.messageName}
        </CurecallConfirmationModal>
      )}

      {variableToShow && (
        <CurecallVariableModal
          variableToShow={variableToShow}
          setVariableToShow={setVariableToShow}
          modalTitle={t('pages.configuration.messages.variable-information', {
            label: variableToShow.label,
          })}
          closeButtonAriaLabel={t('pages.configuration.messages.variable-information', {
            label: variableToShow.label,
          })}
          explainationsText={t('pages.configuration.messages.variable-information-explainations', {
            label: variableToShow.label,
          })}
          forExampleText={t('pages.configuration.messages.for-example')}
          willGiveText={t('pages.configuration.messages.will-give')}
        />
      )}

      {programmedMessageToShow && !variableToShow && (
        <CurecallModalContainer
          closeModalAriaLabel={t('common.actions.close')}
          title={programmedMessageToShow.messageName}
          isOpen={!!programmedMessageToShow}
          toggle={() => setProgrammedMessageToShow(null)}
          dialogClassName="sm:c-w-[750px] xl:c-w-[900px]"
        >
          <p className="c-text-sm c-text-grey-500 c-font-nexa">
            {t('pages.configuration.messages.multiple-sms-disclaimer')}
          </p>
          {isContentPartial(programmedMessageToShow.notEditableReason) && (
            <p className="c-text-sm c-text-orange-800 c-font-nexa">
              {t('pages.configuration.messages.partial-content-warning')}
            </p>
          )}
          <h2 className="c-bg-grey-100 c-text-base c-px-4 c-py-2 c-text-grey-700 c-m-0">
            {t('pages.configuration.messages.message-content')}
          </h2>
          <ul>
            {programmedMessageToShow.profileRules.map((messageContent, ruleIndex) => (
              <li
                key={`${ruleIndex}-${messageContent.medicalStaffMemberId ?? 'all'}-${messageContent.ageGroup ?? 'all'}`}
                className="c-py-4 c-border-b c-border-b-grey-200"
              >
                <MessageContentWithCopyButton
                  programmedMessageId={programmedMessageToShow.id}
                  messageContent={messageContent.message}
                  variableStore={variableStore}
                  contentVariables={contentVariables}
                  defaultContentVariable={defaultContentVariable}
                  setVariableToShow={setVariableToShow}
                  tooltipPosition="right"
                />
              </li>
            ))}
          </ul>
        </CurecallModalContainer>
      )}
    </div>
  )
}
